import { NhlSeasonStats } from './api/nhlPlayers';

export type ValueLabel = 'underpaid' | 'fair' | 'overpaid' | 'unknown';

const DEFAULT_PROJECTED_SEASON_GAMES = 82;
const DEFAULT_MIN_GAMES_FOR_PROJECTION = 10;

interface ProjectionOptions {
  seasonGames?: number;
  minGamesForProjection?: number;
}

export const CONTRACT_VALUE_BADGE_CONFIG: Record<ValueLabel, { bg: string; textKey: string }> = {
  underpaid: { bg: 'bg-success', textKey: 'player.detail.contract.underpaid' },
  fair: { bg: 'bg-warning text-dark', textKey: 'player.detail.contract.fair' },
  overpaid: { bg: 'bg-danger', textKey: 'player.detail.contract.overpaid' },
  unknown: { bg: 'bg-secondary', textKey: 'player.detail.contract.unknown' },
};

export function computeValueLabelFromProduction(
  salary: number,
  production: number | null | undefined,
  isGoalie: boolean
): ValueLabel {
  const productionValue = production ?? 0;
  if (productionValue <= 0) return 'unknown';

  const ratio = salary / productionValue;
  if (isGoalie) {
    if (ratio < 200_000) return 'underpaid';
    if (ratio <= 500_000) return 'fair';
    return 'overpaid';
  }

  if (ratio < 100_000) return 'underpaid';
  if (ratio <= 250_000) return 'fair';
  return 'overpaid';
}

export function computeProjectedProduction(
  production: number | null | undefined,
  gamesPlayed: number | null | undefined,
  options?: ProjectionOptions
): number | null {
  const productionValue = production ?? 0;
  const rawGamesPlayed = gamesPlayed ?? 0;

  if (productionValue <= 0 || rawGamesPlayed <= 0) {
    return null;
  }

  const seasonGames = options?.seasonGames ?? DEFAULT_PROJECTED_SEASON_GAMES;
  const minGamesForProjection = options?.minGamesForProjection ?? DEFAULT_MIN_GAMES_FOR_PROJECTION;
  const adjustedGamesPlayed = Math.max(rawGamesPlayed, minGamesForProjection);

  return productionValue * (seasonGames / adjustedGamesPlayed);
}

export function computeValueLabelFromProjectedProduction(
  salary: number,
  production: number | null | undefined,
  gamesPlayed: number | null | undefined,
  isGoalie: boolean,
  options?: ProjectionOptions
): ValueLabel {
  const projectedProduction = computeProjectedProduction(production, gamesPlayed, options);
  if (!projectedProduction || projectedProduction <= 0) {
    return 'unknown';
  }

  return computeValueLabelFromProduction(salary, projectedProduction, isGoalie);
}

export function computeValueLabelFromSeasonStats(
  salary: number,
  stats: NhlSeasonStats | null,
  isGoalie: boolean
): ValueLabel {
  if (!stats?.seasonTotals?.length) return 'unknown';

  const regularNhl = stats.seasonTotals
    .filter((s) => s.gameTypeId === 2 && s.leagueAbbrev === 'NHL')
    .sort((a, b) => b.season - a.season);

  const latest = regularNhl[0];
  if (!latest) return 'unknown';

  const production = isGoalie ? (latest.wins ?? 0) : (latest.points ?? 0);
  return computeValueLabelFromProjectedProduction(salary, production, latest.gamesPlayed, isGoalie);
}